import moment, {Moment} from 'moment';
import React, {useEffect, useMemo} from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components/macro';
import {Table} from '@/web-components';
import {BUDGET_TYPE, PERIOD_BUDGET_TYPE} from '../../../../../constants';
import {dispatch, EVENT_ID} from '../../../../../containers/event_manager';
import {Connection} from '../../../../../forecast-app/shared/util/type-util/Connection';
import Util from '../../../../../forecast-app/shared/util/util';
import CreateBulkFixedPriceLockMutation from '../../../../../mutations/create_bulk_fixed_price_lock_mutation';
import * as tracking from '../../../../../tracking';
import {trackEvent} from '../../../../../tracking/amplitude/TrackingV2';
import {CurrencyInputWrapper} from '../../../../inputs/currency_input_wrapper';
import {isColumnLocked} from '../util/BudgetUtils';
import {hasPermission} from '../../../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../../../Permissions';

interface EditorWrapperProps {
	isUpdating: boolean;
	hasManualValue: boolean;
}

const EditorWrapper = Styled.div<EditorWrapperProps>`
  width: 100%;
  opacity:  ${props => (props.isUpdating ? '0.5' : '1.0')};  
	.currency-input {
		background-color: ${props => (props.hasManualValue ? '#F7F7FE' : '#FFF')}; // TODO: get the color from a theme provider somehow
	}
`;

type Project = {
	id: unknown;
	budgetType: unknown;
	defaultPeriodBudgetType: unknown;
	fixedPriceLocks: Connection<{id: unknown; startDate: string; endDate: string}>;
};

interface ManualRevenueRecognitionTableRowProps {
	data: {columns: {name: string}[]};
	rowData: {
		currency: unknown;
		aggregate: string;
		dataArray: {startDate: Moment; value: number}[];
		expenseAmounts: number[];
	};
	project: Project;
	tableColumnProps: Record<string, unknown>;
	isUpdating: boolean;
	setUpdating: React.Dispatch<boolean>;
}

const ManualRevenueRecognitionTableRow = ({
	data,
	rowData,
	project,
	tableColumnProps,
	isUpdating,
	setUpdating,
}: ManualRevenueRecognitionTableRowProps): React.JSX.Element => {
	const intl = useIntl();
	const currencySymbol = Util.GetCurrencySymbol(rowData.currency);
	const fieldName = rowData.aggregate;
	const [monthColumns, totalColumn] = useMemo(
		() => [rowData.dataArray.slice(0, -1), rowData.dataArray.at(-1)],
		[rowData.dataArray]
	);

	const renderValue = (value: number): string => {
		if (value !== undefined) {
			return Util.getFormattedNumberWithCurrency(currencySymbol, value, intl);
		} else {
			return '–';
		}
	};

	useEffect(
		() => {
			setUpdating(false);
		},
		rowData.dataArray.map(v => v.value)
	);

	const formatDateISO = (date: Moment) => (date ? date.format('YYYY-MM-DD') : '');

	function allowMoreManualRecognition() {
		if (
			project.budgetType === BUDGET_TYPE.TIME_AND_MATERIALS ||
			(project.budgetType === BUDGET_TYPE.RETAINER &&
				(project.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.TIME_AND_MATERIALS ||
					project.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_HOURS))
		) {
			return true;
		}

		const firstMonthStartDate = formatDateISO(monthColumns[0].startDate);
		const lastMonthStartDate = formatDateISO(monthColumns[monthColumns.length - 1].startDate);
		const relevantFixedPriceLocks = project.fixedPriceLocks.edges.filter(
			e => e.node.endDate > firstMonthStartDate && e.node.startDate <= lastMonthStartDate
		);
		const manuallyRecognizedMonthCount = [...new Set(relevantFixedPriceLocks.map(e => e.node.id))].length; // count distinct IDs (during mutation the same object has two edges)
		const totalMonths = monthColumns.length;
		return totalMonths > manuallyRecognizedMonthCount + 1;
	}

	function isMonthManuallyRecognised(index: number): boolean {
		const monthStartDate = monthColumns[index].startDate;
		return !!project.fixedPriceLocks.edges.find(fixedPriceLock =>
			moment(fixedPriceLock.node.startDate).isSame(monthStartDate)
		);
	}

	const onManualRecognitionUpdated = (index: number, newValue: number, existingValue: number, expenseAmount: number) => {
		tracking.trackPageAction('Manual Recognized Amount Added');
		trackEvent('Manual Recognized Amount', 'Added');

		const periodData = monthColumns[index];
		const valueChanged =
			Util.getFormattedCurrencyNumber(existingValue, intl) !== Util.getFormattedCurrencyNumber(newValue, intl);
		if (valueChanged && periodData && (isMonthManuallyRecognised(index) || allowMoreManualRecognition())) {
			const expenseValue = expenseAmount || 0;
			setUpdating(true);
			Util.CommitMutation(
				CreateBulkFixedPriceLockMutation,
				{
					projectId: project.id,
					locks: [
						{
							startDate: formatDateISO(periodData.startDate.clone().startOf('month')),
							endDate: formatDateISO(periodData.startDate.clone().endOf('month')),
							locked: false,
							amount: newValue + expenseValue,
							timeAmount: newValue,
						},
					],
				},
				() => {
					dispatch(EVENT_ID.BUDGET_UPDATE_PROJECT);
				}
			);
		}
	};

	return (
		<Table.Row cy={'row-' + fieldName} key={fieldName} {...tableColumnProps}>
			<Table.Column>{intl.formatMessage({id: 'project_budget.manual_revenue_recognition'})}</Table.Column>
			{monthColumns.map((month, index) => {
				const isLocked = isColumnLocked(project.fixedPriceLocks, month);
				const isManuallyRecognised = isMonthManuallyRecognised(index);
				const isEditable = !isLocked && allowMoreManualRecognition() && hasPermission(PERMISSION_TYPE.PROJECTS_UPDATE);
				const expenseAmount = rowData.expenseAmounts[index];

				return (
					<Table.Column
						cy={'revenue-recognition-row-' + fieldName + '-column-' + data.columns[index].name}
						key={fieldName + month.value}
						bold={false}
						negative={month.value < 0}
						greyedOut={isLocked}
					>
						<EditorWrapper isUpdating={isUpdating} hasManualValue={isManuallyRecognised}>
							{isEditable ? (
								<CurrencyInputWrapper
									intl={intl}
									value={month.value}
									disabled={isUpdating}
									callback={(value: number) =>
										onManualRecognitionUpdated(index, value, month.value, expenseAmount)
									}
									currencySymbol={currencySymbol}
									className="currency-input"
								/>
							) : (
								<span>{renderValue(month.value)}</span>
							)}
						</EditorWrapper>
					</Table.Column>
				);
			})}

			{totalColumn && (
				<Table.Column
					cy={'revenue-recognition-row-' + fieldName + '-column-Totals'}
					key={fieldName + totalColumn.value}
					bold={true}
					negative={totalColumn.value < 0}
					greyedOut={isUpdating}
				>
					<span>{renderValue(totalColumn.value)}</span>
				</Table.Column>
			)}
		</Table.Row>
	);
};

export default ManualRevenueRecognitionTableRow;
